import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import InputAdornment from '@mui/material/InputAdornment'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup, { BUTTON_LOADING_ICON, FORM_DIALOG_ACTION_BUTTON_SX, FORM_DIALOG_DANGER_ACTION_BUTTON_SX, FORM_DIALOG_WARNING_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import StatusChip, { ORDER_CLAIM_STATUS_CHIP } from '../../component/StatusChip'
import { adminBorders, adminColors, adminRadii, adminTransitions } from '../../theme/adminTokens'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'
import {
    applyOrderClaimDraft,
    clearOrderClaimDraft,
    createEditOrderClaimDraftKey,
    createOrderClaimDraftPayload,
    readOrderClaimDraft,
    saveOrderClaimDraft,
} from './orderClaimDraftUtils'

const CLAIM_ACTION_OPTIONS = [
    {
        value: 'approve',
        title: '승인',
        description: '예상 복구 정보를 확인한 뒤 클레임을 승인 처리합니다.',
        color: 'primary',
    },
    {
        value: 'reject',
        title: '반려',
        description: '고객에게 표시될 반려 사유를 입력한 뒤 반려 처리합니다.',
        color: 'error',
    },
]

const getClaimActionCardSx = (selected, disabled = false, color = 'primary') => ({
    m: 0,
    p: 1.5,
    alignItems: 'flex-start',
    borderRadius: `${adminRadii.control}px`,
    border: '1px solid',
    width: '100%',
    borderColor: selected ? `${color}.main` : adminBorders.subtle,
    bgcolor: selected ? (color === 'error' ? 'error.light' : adminColors.blueHover) : 'background.paper',
    boxShadow: selected ? `inset 0 0 0 1px ${color === 'error' ? adminColors.redBorder : adminColors.blueSelected}` : 'none',
    transition: `border-color ${adminTransitions.fast}, background-color ${adminTransitions.fast}, box-shadow ${adminTransitions.fast}`,
    opacity: disabled ? 0.52 : 1,
    '&:hover': {
        borderColor: disabled ? adminBorders.subtle : selected ? `${color}.main` : adminBorders.strong,
    },
    '& .MuiRadio-root': {
        mt: -0.25,
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
})

function ClaimActionLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 관리자 처리 액션은 API에 보낼 action 문자열을 그대로 선택값으로 사용한다.
function ClaimActionChoiceGroup({
    value,
    disabled,
    onChange,
}) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                처리 방법
            </Typography>
            <Box
                role="radiogroup"
                aria-label="처리 방법"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {CLAIM_ACTION_OPTIONS.map((option) => {
                    const selected = value === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name="action"
                                    checked={selected}
                                    onChange={() => onChange(option.value)}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getClaimActionCardSx(selected, disabled, option.color)}
                            label={(
                                <ClaimActionLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

export default function OrderClaim(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.orderClaimId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText
    const searchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const returnTarget = locationState.returnTarget
    const draftKey = createEditOrderClaimDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState(null)
    const [action, setAction] = useState('approve')
    const [rejectReason, setRejectReason] = useState('')
    const [adminMemo, setAdminMemo] = useState('')
    const [shippingAmount, setShippingAmount] = useState(0) // 관리자가 조정 가능한 반품 배송비
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [withdrawRejectDialogOpen, setWithdrawRejectDialogOpen] = useState(false)
    const [withdrawRejectReason, setWithdrawRejectReason] = useState('')
    const [withdrawingReject, setWithdrawingReject] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const REJECT_REASON_MAX_BYTES = 1000 // 반려 사유 DB 바이트 제한(varchar(1000))과 정합
    const WITHDRAW_REJECT_REASON_MAX_LENGTH = 255
    const isApproving = saving && action === 'approve'
    const isRejecting = saving && action === 'reject'
    const submitButtonLabel = saving
        ? (action === 'approve' ? '승인 처리 중...' : '반려 처리 중...')
        : (action === 'approve' ? '승인 처리' : '반려 처리')
    // 반려 사유는 한글/이모지 입력을 고려해 문자 수가 아닌 UTF-8 바이트 기준으로 제한한다.

    const getUtf8ByteLength = (value) => {
        if (typeof TextEncoder === 'undefined') {
            return String(value || '').length // 구형 환경 폴백
        }
        return new TextEncoder().encode(String(value || '')).length
    }

    const getRejectReasonByteLength = () => getUtf8ByteLength(rejectReason)

    // 상세 내 금액/포인트 표기는 공통 숫자 포맷에 단위만 붙인다.
    const formatCurrency = (value) => `${formatNumber(value)}원`
    const formatPoint = (value) => `${formatNumber(value)}P`

    const resetDetailState = useCallback((errorMessage) => {
        setData(null)
        setAction('approve')
        setRejectReason('')
        setAdminMemo('')
        setShippingAmount(0)
        setDraftReady(false)
        setWithdrawRejectDialogOpen(false)
        setWithdrawRejectReason('')
        setWithdrawingReject(false)
        setDetailErrorMessage(errorMessage)
    }, [])
    
    const truncateUtf8Bytes = (value = '', maxBytes = REJECT_REASON_MAX_BYTES) => {
        let nextValue = String(value || '')
        if (getUtf8ByteLength(nextValue) <= maxBytes) return nextValue
        let end = nextValue.length

        // 바이트 초과 시 마지막 글자부터 줄여 DB 제한 바이트 이내로 보정한다.
        while (end > 0 && getUtf8ByteLength(nextValue.substring(0, end)) > maxBytes) {
            end -= 1
        }
        return nextValue.substring(0, end)
    }

    const getData = useCallback(async ({ showLoading = true } = {}) => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 환불/반품 상세를 불러올 수 없습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        if (showLoading) {
            setLoading(true)
        }
        setDraftReady(false)
        setDetailErrorMessage('')

        try {
            const { data: response } = await API.post('/api/v1/admin/orderClaim/getDetail', { orderClaimId: id })

            if (response.code === 200) {
                const detailData = response.data || {}

                if (!detailData.claimInfo?.orderClaimId) {
                    resetDetailState('환불/반품 상세 정보를 찾을 수 없습니다.')
                    return
                }

                const baseDraft = createOrderClaimDraftPayload({
                    action: 'approve',
                    rejectReason: '',
                    adminMemo: detailData.claimInfo.adminMemo || '',
                    shippingAmount: Number(
                        detailData.refundInfo?.recommendedReturnShippingAmount
                        ?? detailData.refundInfo?.returnShippingAmount
                        ?? 0
                    ),
                })
                const storedDraft = readOrderClaimDraft(draftKey)
                const restoredDraft = applyOrderClaimDraft({
                    baseDraft,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(baseDraft)

                setData(detailData)
                setAction(restoredDraft.action)
                setRejectReason(restoredDraft.rejectReason)
                setAdminMemo(restoredDraft.adminMemo)
                const maxShippingAmount = Number(
                    detailData.refundInfo?.maxReturnShippingAmount
                    ?? detailData.refundInfo?.maxShippingAmount
                    ?? 0
                )
                setShippingAmount(Math.min(restoredDraft.shippingAmount, maxShippingAmount)) // ⭐ 초기값 설정
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (response.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = response.message || '환불/반품 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '환불/반품 상세를 불러오지 못했습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady || !data?.claimInfo?.orderClaimId) {
            return
        }

        if (data.claimInfo.status === 3 || data.claimInfo.status === 4) {
            clearOrderClaimDraft(draftKey)
            return
        }

        // 클레임 상세는 목록 query를 건드리지 않고 처리 draft만 sessionStorage에 보존한다.
        const nextDraft = createOrderClaimDraftPayload({
            action,
            rejectReason,
            adminMemo,
            shippingAmount,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearOrderClaimDraft(draftKey)
            return
        }

        saveOrderClaimDraft(draftKey, nextDraft)
    }, [action, adminMemo, data, draftKey, draftReady, rejectReason, shippingAmount])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 결제 상세에서 진입한 경우엔 원래 상세로, 아니면 기존 클레임 목록으로 복귀한다.
    const buildReturnTarget = (snackbarState) => {
        if (returnTarget?.pathname) {
            const nextState = {
                ...(returnTarget.state || {}),
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {})
            }

            return {
                pathname: returnTarget.pathname,
                search: returnTarget.search || '',
                state: nextState,
                ...nextState,
            }
        }

        return createListLinkTarget({
            pathname: '/orderClaims',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        })
    }

    // 처리 후에도 운영자가 보던 클레임 목록 조건으로 정확히 돌아가게 맞춘다.
    const moveToList = (snackbarState) => {
        clearOrderClaimDraft(draftKey)
        history.push(buildReturnTarget(snackbarState))
    }

    // 배송비 변경 핸들러
    const handleShippingAmountChange = (e) => {
        const value = parseInt(e.target.value) || 0
        setShippingAmount(value)
    }

    const openWithdrawRejectDialog = () => {
        setWithdrawRejectReason('')
        setWithdrawRejectDialogOpen(true)
    }

    const closeWithdrawRejectDialog = () => {
        if (withdrawingReject) {
            return
        }

        setWithdrawRejectDialogOpen(false)
        setWithdrawRejectReason('')
    }

    const handleWithdrawRejectReasonChange = (e) => {
        setWithdrawRejectReason(String(e.target.value || '').slice(0, WITHDRAW_REJECT_REASON_MAX_LENGTH))
    }

    const handleWithdrawRejectSubmit = async () => {
        if (withdrawingReject) {
            return
        }

        const claimInfoForWithdraw = data?.claimInfo || {}
        if (Number(claimInfoForWithdraw.claimType) !== 3 || Number(claimInfoForWithdraw.status) !== 4) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('반품 반려 건만 철회할 수 있습니다.')
            return
        }

        const trimmedReason = String(withdrawRejectReason || '').trim()
        if (!trimmedReason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('철회 사유를 입력해주세요.')
            return
        }

        if (trimmedReason.length > WITHDRAW_REJECT_REASON_MAX_LENGTH) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('철회 사유는 255자 이하로 입력해주세요.')
            return
        }

        try {
            setWithdrawingReject(true)

            const { data: response } = await API.post('/api/v1/admin/orderClaim/withdrawReject', {
                orderClaimId: id,
                reason: trimmedReason,
            })

            if (response.code === 200) {
                clearOrderClaimDraft(draftKey)
                setWithdrawRejectDialogOpen(false)
                setWithdrawRejectReason('')
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('반품 반려를 철회했습니다. 기존 승인 처리로 반품 완료를 진행하세요.')
                await getData({ showLoading: false })
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message || '반품 반려를 철회하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('반품 반려를 철회하지 못했습니다.')
        } finally {
            setWithdrawingReject(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        // 반려 시 사유 필수
        const trimmedRejectReason = String(rejectReason || '').trim()
        if (action === 'reject' && !trimmedRejectReason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('반려 사유를 입력하세요.')
            return
        }

        // 반려 사유는 DB의 varchar(1000) 제한과 맞춰 1000바이트 초과를 차단
        if (action === 'reject' && getUtf8ByteLength(trimmedRejectReason) > REJECT_REASON_MAX_BYTES) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('반려 사유는 1000바이트 이내로 입력해 주세요.')
            return
        }

        // 배송비 음수 체크
        if (shippingAmount < 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('배송비는 0원 이상이어야 합니다.')
            return
        }

        const maxShippingAmount = Number(
            data?.refundInfo?.maxReturnShippingAmount
            ?? data?.refundInfo?.maxShippingAmount
            ?? 0
        )
        if (action === 'approve' && shippingAmount > maxShippingAmount) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar(`추가 차감 가능한 배송비는 최대 ${formatCurrency(maxShippingAmount)}입니다.`)
            return
        }

        try {
            setSaving(true)

            const { data: response } = await API.post('/api/v1/admin/orderClaim/update', {
                orderClaimId: id,
                action: action,
                rejectReason: trimmedRejectReason,
                adminMemo: adminMemo,
                returnShippingAmount: shippingAmount
            })

            if (response.code === 200) {
                moveToList({
                    severity: 'success',
                    message: action === 'approve' ? '클레임을 승인 처리했습니다.' : '클레임을 반려 처리했습니다.',
                })
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('클레임 상태를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="환불/반품 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <div>Loading...</div>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!data) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Title>환불/반품 신청 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    const { claimInfo, userInfo, orderInfo, items, refundInfo, bankInfo, restoreInfo = {} } = data

    // 처리 완료/반려 상태면 수정 불가
    const isProcessed = claimInfo.status === 3 || claimInfo.status === 4
    const isRejectedReturn = Number(claimInfo.claimType) === 3 && Number(claimInfo.status) === 4

    const serverShippingAdjustmentAmount = Number(
        refundInfo.serverShippingAdjustmentAmount
        ?? refundInfo.outboundShippingAdjustmentAmount
        ?? 0
    )
    const totalShippingAdjustmentAmount = serverShippingAdjustmentAmount + Number(shippingAmount || 0)
    const calculatedRefundAmount = Number(refundInfo.totalClaimAmount || 0) - totalShippingAdjustmentAmount
    const alreadyDeductedShippingAmount = Number(refundInfo.alreadyDeductedShippingAmount || 0)
    const maxShippingAmount = Number(
        refundInfo.maxReturnShippingAmount
        ?? refundInfo.maxShippingAmount
        ?? 0
    )
    const recommendedReturnShippingAmount = Number(
        refundInfo.recommendedReturnShippingAmount
        ?? refundInfo.returnShippingAmount
        ?? 0
    )
    const editableReturnShipping = Boolean(refundInfo.editableReturnShipping)
    const expectedPointRestore = Number(restoreInfo.expectedPointRestore || 0)
    const expectedCouponRestore = Boolean(restoreInfo.expectedCouponRestore)
    const formatShippingAdjustment = (value) => {
        const amount = Number(value || 0)
        if (amount > 0) return `-${formatCurrency(amount)}`
        if (amount < 0) return `+${formatCurrency(Math.abs(amount))}`
        return formatCurrency(0)
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                <Title>환불/반품 신청 상세</Title>
                
                <Divider/>

                <Alert severity="info" sx={{ mt: 3 }}>
                    고객이 직접 접수한 원본 신청 정보입니다. 승인 또는 반려 전에 신청 사유, 수량, 환불 계좌 정보를 먼저 확인하세요.
                </Alert>

                {/* 클레임 기본 정보 */}
                <Box sx={{ mt: 3, mb: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">신청 타입</Typography>
                            <StatusChip label={claimInfo.claimTypeName} color="primary" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">처리 상태</Typography>
                            <StatusChip status={claimInfo.status} config={ORDER_CLAIM_STATUS_CHIP} label={claimInfo.statusName} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">클레임 번호</Typography>
                            <Typography variant="body1">{claimInfo.orderClaimNo}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">주문 번호</Typography>
                            <Typography variant="body1">{claimInfo.orderId}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">신청일시</Typography>
                            <Typography variant="body1">{claimInfo.regTime}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">처리일시</Typography>
                            <Typography variant="body1">{claimInfo.processTime && claimInfo.processTime}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 고객 신청 원본 정보 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>고객 신청 원본 정보</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">신청자</Typography>
                            <Typography variant="body1">{userInfo.userName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">회원 아이디</Typography>
                            <Typography variant="body1">{userInfo.userId}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">연락처</Typography>
                            <Typography variant="body1">{userInfo.userPhone}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">이메일</Typography>
                            <Typography variant="body1">{userInfo.userEmail}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">신청 타입</Typography>
                            <Typography variant="body1">{claimInfo.claimTypeName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">사유 유형</Typography>
                            <Typography variant="body1">{claimInfo.claimReasonTypeName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">신청일시</Typography>
                            <Typography variant="body1">{claimInfo.regTime}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">결제수단</Typography>
                            <Typography variant="body1">
                                {orderInfo.payMethod === 'card' ? '신용카드' :
                                 orderInfo.payMethod === 'vbank' ? '가상계좌' :
                                 orderInfo.payMethod === 'trans' ? '계좌이체' : orderInfo.payMethod}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">고객 입력 사유</Typography>
                            <Typography variant="body1">{claimInfo.claimReason}</Typography>
                        </Grid>
                        {bankInfo.accountNumber && (
                            <>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary">환불 은행</Typography>
                                    <Typography variant="body1">{bankInfo.bankName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary">환불 계좌번호</Typography>
                                    <Typography variant="body1">{bankInfo.accountNumber}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary">예금주명</Typography>
                                    <Typography variant="body1">{bankInfo.depositorName}</Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 사용자 정보 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>사용자 정보</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">이름</Typography>
                            <Typography variant="body1">{userInfo.userName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">아이디</Typography>
                            <Typography variant="body1">{userInfo.userId}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">전화번호</Typography>
                            <Typography variant="body1">{userInfo.userPhone}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">이메일</Typography>
                            <Typography variant="body1">{userInfo.userEmail}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 주문 정보 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>주문 정보</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">결제일시</Typography>
                            <Typography variant="body1">{orderInfo.pgTime}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">결제 수단</Typography>
                            <Typography variant="body1">
                                {orderInfo.payMethod === 'card' ? '신용카드' : 
                                 orderInfo.payMethod === 'vbank' ? '가상계좌' : 
                                 orderInfo.payMethod === 'trans' ? '계좌이체' : orderInfo.payMethod}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">총 결제 금액</Typography>
                            <Typography variant="body1">{formatCurrency(orderInfo.totalPayPrice)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">아임포트 ID</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{orderInfo.impId}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 클레임 사유 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>클레임 사유 및 판단 정보</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">사유 유형</Typography>
                            <Typography variant="body1">{claimInfo.claimReasonTypeName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={9} md={9}>
                            <Typography variant="subtitle2" color="text.secondary">상세 사유</Typography>
                            <Typography variant="body1">{claimInfo.claimReason}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 클레임 상품 목록 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>클레임 상품 목록</Typography>
                    <EmbeddedTableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>상품명</TableCell>
                                    <TableCell>카테고리</TableCell>
                                    <TableCell>옵션</TableCell>
                                    <TableCell align="right">원수량</TableCell>
                                    <TableCell align="right">클레임수량</TableCell>
                                    <TableCell align="right">클레임금액</TableCell>
                                    <TableCell align="right">복구포인트</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow key={item.orderClaimDetailId}>
                                        <TableCell>{item.productName}</TableCell>
                                        <TableCell>{item.productCategoryName}</TableCell>
                                        <TableCell>{item.productOptionName || '-'}</TableCell>
                                        <TableCell align="right">{item.originalQuantity}</TableCell>
                                        <TableCell align="right">{item.claimQuantity}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.claimAmount)}</TableCell>
                                        <TableCell align="right">{formatPoint(item.rewardPoint)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </EmbeddedTableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 환불 금액 정보 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>환불 금액 정보</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">총 클레임 금액</Typography>
                            <Typography variant="body1">{formatCurrency(refundInfo.totalClaimAmount)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">복구 포인트</Typography>
                            <Typography variant="body1" color="primary">+{formatPoint(refundInfo.totalRewardPoint)}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">출고 배송비 조정</Typography>
                            <Typography variant="body1" color={serverShippingAdjustmentAmount > 0 ? 'error' : serverShippingAdjustmentAmount < 0 ? 'primary' : 'text.primary'}>
                                {formatShippingAdjustment(serverShippingAdjustmentAmount)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">서버 계산값으로 수정할 수 없습니다.</Typography>
                        </Grid>

                        {!isProcessed ? (
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="반품 배송비 차감"
                                    value={shippingAmount}
                                    onChange={handleShippingAmountChange}
                                    size="small"
                                    disabled={saving || !editableReturnShipping}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">원</InputAdornment>,
                                    }}
                                    inputProps={{ min: 0, max: maxShippingAmount }}
                                    error={shippingAmount > maxShippingAmount}
                                    helperText={editableReturnShipping
                                        ? `관리자 조정 가능: 최대 ${formatCurrency(maxShippingAmount)}`
                                        : '이 클레임에서는 수정할 수 없습니다.'}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="subtitle2" color="text.secondary">반품 배송비 차감</Typography>
                                <Typography variant="body1" color="error">-{formatCurrency(refundInfo.returnShippingAmount)}</Typography>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">최종 배송비 조정</Typography>
                            <Typography variant="body1" color={totalShippingAdjustmentAmount > 0 ? 'error' : totalShippingAdjustmentAmount < 0 ? 'primary' : 'text.primary'}>
                                {formatShippingAdjustment(totalShippingAdjustmentAmount)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">예상 환불 금액</Typography>
                            <Typography variant="h6" color="primary">
                                {formatCurrency(calculatedRefundAmount)}
                            </Typography>
                        </Grid>
                    </Grid>

                    {!isProcessed && Number(claimInfo.claimType) === 3 && (
                        <Alert severity={alreadyDeductedShippingAmount > 0 ? 'warning' : 'info'} sx={{ mt: 2 }}>
                            이 주문에서 이미 차감한 반품 배송비는 {formatCurrency(alreadyDeductedShippingAmount)}이며,
                            이번 건에서 조정 가능한 반품 배송비는 최대 {formatCurrency(maxShippingAmount)}입니다.
                        </Alert>
                    )}

                    {!isProcessed && editableReturnShipping && shippingAmount !== recommendedReturnShippingAmount && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            반품 배송비가 {formatCurrency(recommendedReturnShippingAmount)}에서 {formatCurrency(shippingAmount)}으로 변경됩니다.
                            최종 환불 금액: {formatCurrency(calculatedRefundAmount)}
                        </Alert>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>승인 시 복구 예상</Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        승인 시점 기준 예상 복구 정보입니다. 실제 복구 여부는 클레임 타입, 귀책 사유, 전체 반품 여부에 따라 결정됩니다.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">예상 포인트 복구</Typography>
                            <Typography variant="body1" color="primary">+{formatPoint(expectedPointRestore)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">예상 쿠폰 복구</Typography>
                            <Typography variant="body1" color={expectedCouponRestore ? 'primary' : 'text.primary'}>
                                {expectedCouponRestore ? '복구됨' : '복구 안됨'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">귀책 구분</Typography>
                            <Typography variant="body1">{restoreInfo.responsibilityName || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">전체 반품 여부</Typography>
                            <Typography variant="body1">{restoreInfo.fullReturnStatusName || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">복구 판단 기준</Typography>
                            <Typography variant="body1">{restoreInfo.restoreReason || '-'}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* 환불 계좌 정보 */}
                {bankInfo.accountNumber && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>환불 계좌 정보</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary">은행명</Typography>
                                    <Typography variant="body1">{bankInfo.bankName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary">계좌번호</Typography>
                                    <Typography variant="body1">{bankInfo.accountNumber}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary">예금주명</Typography>
                                    <Typography variant="body1">{bankInfo.depositorName}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                )}

                {/* 반려 사유 */}
                {claimInfo.status === 4 && claimInfo.rejectReason && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom color="error">반려 사유</Typography>
                            <Typography variant="body1">{claimInfo.rejectReason}</Typography>
                        </Box>
                    </>
                )}

                <Divider sx={{ my: 3 }} />

                {/* 처리 폼 */}
                {!isProcessed && (
                    <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                        <Typography variant="h6" gutterBottom>관리자 처리</Typography>
                        
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    승인 시 예상 복구: 포인트 +{formatPoint(expectedPointRestore)} / 쿠폰 {expectedCouponRestore ? '복구됨' : '복구 안됨'}
                                </Alert>
                            </Grid>
                            <Grid item xs={12}>
                                <ClaimActionChoiceGroup
                                    value={action}
                                    disabled={saving}
                                    onChange={setAction}
                                />
                            </Grid>

                            {action === 'reject' && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        inputProps={{ maxLength: REJECT_REASON_MAX_BYTES }} // 브라우저 입력 길이 제한(문자 단위 완충)
                                        multiline
                                        rows={3}
                                        label="반려 사유"
                                        value={rejectReason}
                                        placeholder="고객에게 표시될 반려 사유를 입력하세요."
                                        onChange={(e) => setRejectReason(truncateUtf8Bytes(e.target.value))}
                                        helperText={`반려 사유: ${getRejectReasonByteLength()} / ${REJECT_REASON_MAX_BYTES}바이트(UTF-8)`}
                                        disabled={saving}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="관리자 메모 (선택)"
                                    value={adminMemo}
                                    onChange={(e) => setAdminMemo(e.target.value)}
                                    placeholder="내부용 메모 (고객에게 표시되지 않음)"
                                    disabled={saving}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    <Button 
                                        variant="outlined" 
                                        sx={FORM_DIALOG_ACTION_BUTTON_SX}
                                        disabled={saving}
                                        onClick={() => moveToList()}
                                    >
                                        취소
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="contained" 
                                        color={action === 'approve' ? 'primary' : 'error'}
                                        sx={action === 'approve' ? FORM_DIALOG_ACTION_BUTTON_SX : FORM_DIALOG_DANGER_ACTION_BUTTON_SX}
                                        disabled={saving}
                                        startIcon={(isApproving || isRejecting) ? BUTTON_LOADING_ICON : undefined}
                                        aria-busy={isApproving || isRejecting}
                                    >
                                        {submitButtonLabel}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* 이미 처리된 경우 */}
                {isProcessed && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center'}}>
                        {isRejectedReturn && (
                            <Button
                                variant="contained"
                                color="warning"
                                sx={FORM_DIALOG_WARNING_ACTION_BUTTON_SX}
                                disabled={withdrawingReject}
                                onClick={openWithdrawRejectDialog}
                                startIcon={withdrawingReject ? BUTTON_LOADING_ICON : undefined}
                                aria-busy={withdrawingReject}
                            >
                                반려 철회
                            </Button>
                        )}
                        <Button 
                            variant="outlined" 
                            onClick={() => moveToList()}
                            disabled={withdrawingReject}
                        >
                            목록으로
                        </Button>
                    </Box>
                )}

                <Dialog open={withdrawRejectDialogOpen} onClose={closeWithdrawRejectDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>반려 철회</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            반품 반려 상태를 접수 상태로 되돌립니다. 이후 기존 승인 처리에서 PG 취소와 DB 후처리가 실행됩니다.
                        </Alert>
                        <TextField
                            fullWidth
                            required
                            autoFocus
                            multiline
                            rows={3}
                            label="철회 사유"
                            value={withdrawRejectReason}
                            onChange={handleWithdrawRejectReasonChange}
                            placeholder="운영 이력에 남길 철회 사유를 입력하세요."
                            helperText={`철회 사유: ${withdrawRejectReason.length} / ${WITHDRAW_REJECT_REASON_MAX_LENGTH}자`}
                            disabled={withdrawingReject}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            sx={FORM_DIALOG_ACTION_BUTTON_SX}
                            disabled={withdrawingReject}
                            onClick={closeWithdrawRejectDialog}
                        >
                            취소
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            sx={FORM_DIALOG_WARNING_ACTION_BUTTON_SX}
                            disabled={withdrawingReject || !String(withdrawRejectReason || '').trim()}
                            startIcon={withdrawingReject ? BUTTON_LOADING_ICON : undefined}
                            aria-busy={withdrawingReject}
                            onClick={handleWithdrawRejectSubmit}
                        >
                            {withdrawingReject ? '철회 처리 중...' : '철회 처리'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
